import SubmitButton from '@/components/forms/SubmitButton'
import TextInput from '@/components/forms/TextInput'
import {useRef} from 'react'
import {PhoneNumberUtil, PhoneNumberFormat} from 'google-libphonenumber'
import {event} from 'nextjs-google-analytics'
import React from 'react'
import {
  Formik,
  Form,
  Field,
  FormikHelpers,
  useFormikContext,
} from 'formik'
import {useRouter} from 'next/router'
import toast from 'react-hot-toast'
import CustomToast from '@/components/CustomToast'
import {GuestSchema, RegisterSchema, RegisterValues} from '../../../forms/Register'
import {useMutation} from 'react-query'
import PhoneInput from '../../forms/PhoneInput'
import {AnimatePresence, motion} from 'framer-motion'
import * as fbq from '@/services/fbpixel'
import useAuth from '@/hooks/useAuth'
import { VerificationData } from '@/types'
import { useTranslation } from 'next-i18next'
import { GuestSigning } from '@/services/api/auth'
import { GuestValues } from '../../../forms/Register'
import { fetchCart } from 'CartActions'
import { useCartDispatch } from '@/contexts/CartContext'

interface GuestViewProps {
    currentView: string
    headingNumber: string | null
    shouldVerify: boolean
    setShouldVerify: React.Dispatch<React.SetStateAction<boolean>>
    verificationData: VerificationData | undefined
    setVerificationData: React.Dispatch<React.SetStateAction<VerificationData | undefined>>
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    VerificationError: any
    setVerificaitonError: React.Dispatch<React.SetStateAction<any>>
    setVerificationError: any
    isOpen: boolean
    setCurrentView: any
    postLoginCallBack: any
    setPortLoginCallBack: any
    productType: any
    setProductType: any
    executePostLoginCallback: () => void
    onButtonClick: (e: React.MouseEvent, action: () => void) => void
    name?:any
}

const GuestView = (
    {
        currentView,
        headingNumber,
        verificationData,
        shouldVerify,
        setShouldVerify,
        setIsOpen,
        setVerificationData,
        VerificationError,
        setVerificaitonError,
        setVerificationError,
        isOpen,
        setCurrentView,
        postLoginCallBack,
        setPortLoginCallBack,
        productType,
        setProductType,
        executePostLoginCallback,
        onButtonClick,
        name
    }: GuestViewProps
) => {
    const { t: tCommon } = useTranslation('common');
    const mutation = useMutation(GuestSigning);
    const [iso2, setIso2] = React.useState('IL');

    const { mutate: mutateUser } = useAuth('none');
    const cartDispatch = useCartDispatch();
      const handleSubmit = async (
        values: GuestValues,
        actions: FormikHelpers<GuestValues>,
      ) => {
        const phoneUtil = PhoneNumberUtil.getInstance()
        const parsedNumber = phoneUtil.parseAndKeepRawInput(values.phone, iso2)
        const isValid = phoneUtil.isValidNumber(parsedNumber)

        if (!isValid) {
          actions.setFieldError('phone', 'Phone Number is Invalid')
          return
        }

        mutation.mutate(
          { referral: localStorage.getItem('referral'), ...values },
          {
            onSuccess(data) {
              event('guest-signup')
              fbq.event('GuestSignup', { content_name: values.name })

              // Update authentication state
                if (data && data.user) {
                    mutateUser(data.user, false); // optimistically set user
                    mutateUser(); // revalidate
                } else {
                    mutateUser(); // just revalidate
                }

                // Store token if not already stored in GuestSigning
                if (data && data.token) {
                    localStorage.setItem('auth_token', data.token);
                }

                cartDispatch(fetchCart());

                setIsOpen(false);
                if (executePostLoginCallback) {
                    executePostLoginCallback();
                }

                toast.success(tCommon('guestSessionCreated') || 'Logged In as guest successully');
            },
            onError(error: any) {
              if (error.response?.data?.message === 'Phone Number already Exists') {
                actions.setFieldError('phone', error.response.data.message)
              } else if (error.response?.data?.message === 'The email has already been taken.') {
                actions.setFieldError('email', error.response.data.message)
              } else {
                actions.setFieldError('name', error.response.data.message)
              }

              toast.custom(
                (t) => (
                  <CustomToast t={t} message={error.response.data.message} type="error" />
                ),
                { duration: 4000, id: 'Registration Error' }
              )
            },
          }
        )
      }
    
      const ref = useRef(null)

      return (
        <div className="modal-content p-3 lg:p-6 xl:p-8 2xl:p-11 pb-0 lg:pb-0 xl:pb-0 2xl:pb-0 pt-0 lg:pt-0 xl:pt-0 2xl:pt-0">
                          <AnimatePresence>
                            <motion.div
                              initial={{opacity: 0, translateX: '10%'}}
                              transition={{type: 'spring', duration: 0.5}}
                              animate={{opacity: 1, translateX: '0'}}
                              exit={{opacity: 0, translateX: '10%'}}>
                                  <AnimatePresence>
                                    <motion.div
                                      initial={{opacity: 0, translateX: '10%'}}
                                      transition={{type: 'spring', duration: 0.5}}
                                      animate={{opacity: 1, translateX: '0'}}
                                      exit={{opacity: 0, translateX: '10%'}}>
                                      <>
                                        <Formik<GuestValues>
                                          initialValues={{
                                            name: '',
                                            email: '',
                                            phone: '',
                                            country_code: '+971',
                                          }}
                                          innerRef={ref}
                                          validationSchema={GuestSchema}
                                          validateOnChange={false}
                                          validateOnBlur={false}
                                          onSubmit={handleSubmit}>
                                          {({
                                            errors,
                                            touched,
                                            values,
                                            setFieldValue,
                                            setFieldError,
                                          }) => (
                                            <GuestForm
                                              errors={errors}
                                              touched={touched}
                                              values={values}
                                              setFieldValue={setFieldValue}
                                              setFieldError={setFieldError}
                                              mutation={mutation}
                                              setIso2={setIso2}
                                              onButtonClick={onButtonClick}
                                              name={name}
                                            />
                                          )}
                                        </Formik>
                                      </>
                                    </motion.div>
                                  </AnimatePresence>
                            </motion.div>
                          </AnimatePresence>
                        </div>
      )
}

export default GuestView;

function GuestForm({
  errors,
  touched,
  setFieldError,
  setFieldValue,
  setIso2,
  values,
  mutation,
  onButtonClick,
  name
}: any) {
  const {t: tCommon} = useTranslation('common');
  const {locale} = useRouter();
  return (
    <Form>
      <div className="col lg:flex flex-row" id='register-form'>
        <div className="form-group flex flex-col mt-3 w-full">
          <TextInput
            label={`${tCommon('guestNameLabel')}`}
            type="text"
            name="name"
            placeholder={`${tCommon('guestNamePlaceholder')}`}
            isError={errors.name && touched.name}
            error={errors.name}
            dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
          />
        </div>
      </div>

      <div className="form-group flex flex-col mt-3">
        <TextInput
          label={`${tCommon('guestEmailLabel')}`}
          type="email"
          name="email"
          placeholder={`${tCommon('guestEmailPlaceholder')}`}
          isError={errors.email && touched.email}
          error={errors.email}
          dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
        />
      </div>

      <div className="form-group flex flex-col mt-3" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
        <PhoneInput
          values={values}
          setFieldError={setFieldError}
          setFieldValue={setFieldValue}
          setIso2={setIso2}
          isError={errors.phone && touched.phone}
          error={errors.phone}
          label={`${tCommon('guestPhoneLabel')}`}
          placeholder={`${tCommon('guestPhonePlaceholder')}`}
        />
      </div>

      <SubmitButton
        text={{
          text1: `${tCommon('guestButtonText1')}`,
          focusText: `${tCommon('guestButtonText2', {name})}`,
          text2: `${tCommon('guestButtonText3')}`
        }}
        textType="multi-string"
        isLoading={mutation.isLoading}
      />
    </Form>
  )
}